import React, { memo, useState, useCallback, useMemo, useEffect } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';
import { Link, useLocation } from 'react-router-dom';
import { Badge } from '@mui/material';
import { CollectorScope } from 'graphql/types';
import { useMeContext } from 'modules/me';
import { useSubstituteUser } from 'modules/rootPage/hooks/useSubstituteUser';
import { useFeatureFlag } from 'modules/common/hooks';
import { UserCollectorsTab } from './UserCollectorsTab';
import { CompanyCollectorsTab } from './CompanyCollectorsTab';
import { InstalledCollectorsTab } from './InstalledCollectorsTab';
import { AllCollectorsTab } from './AllCollectorsTab';
import { useCollectorSyncStatusContext } from './contexts/CollectorSyncStatusContextProvider';
import { useCollectorSelectionContext } from './contexts/CollectorSelectionContextProvide';

type PageStyleProps = {
  isSubstituteUser: boolean;
};

const useStyles = makeStyles((theme) => ({
  box: {
    background: theme.palette.information.pageBackground,
    height: ({ isSubstituteUser }: PageStyleProps) =>
      `calc(100% - ${theme.spacing(isSubstituteUser ? 12 : 8)})`
  },
  tabs: {
    borderBottom: `solid 1px ${theme.palette.grey[300]}`,
    background: theme.palette.common.white
  },
  tab: {
    width: theme.spacing(27)
  },
  indicator: {
    display: 'flex',
    justifyContent: 'center'
  },
  badge: {
    '& .MuiBadge-badge': {
      backgroundColor: theme.palette.error.dark,
      bottom: theme.spacing(1.2),
      left: theme.spacing(-1.2)
    }
  }
}));

type TabPanelProps = {
  children?: React.ReactNode;
};

const TabPanel: React.FC<TabPanelProps> = ({ children }: TabPanelProps) => {
  const { isSubstituteUser } = useSubstituteUser();
  const classes = useStyles({ isSubstituteUser });

  return (
    <Box paddingTop={1} className={classes.box}>
      {children}
    </Box>
  );
};

export const CollectorListTabs: React.FC = memo(() => {
  const { isSubstituteUser } = useSubstituteUser();
  const classes = useStyles({ isSubstituteUser });
  const isTiwUnifiedCollectorsTab = useFeatureFlag('isTiwUnifiedCollectorsTab');
  const me = useMeContext();
  const location = useLocation();
  const { hash } = location;
  const INSTALLED_TAB_ID = 'INSTALLED';

  const isAdmin = useMemo(() => {
    return me?.permissions.hasCompanyCollectorInstallAccess ? true : false;
  }, [me]);

  const idHashValues: any = useMemo(
    () => ({
      '#user': CollectorScope.User,
      '#company': isAdmin ? CollectorScope.Tenant : null,
      '#installed': INSTALLED_TAB_ID,
      '#home': CollectorScope.All
    }),
    [isAdmin]
  );

  const DEFAULT_ID = useMemo(
    () =>
      isTiwUnifiedCollectorsTab
        ? CollectorScope.All
        : isAdmin
        ? CollectorScope.Tenant
        : CollectorScope.User,
    [isAdmin, isTiwUnifiedCollectorsTab]
  );

  const setTabIdValue = useCallback(() => {
    if (hash) {
      return idHashValues[hash] || DEFAULT_ID;
    } else {
      return DEFAULT_ID;
    }
  }, [idHashValues, DEFAULT_ID, hash]);

  const [scope, setScope] = useState(setTabIdValue());
  useEffect(() => {
    setScope(setTabIdValue());
  }, [setTabIdValue]);

  const { collectorsSelectionStatus } = useCollectorSelectionContext();
  const { collectorsSyncStatus } = useCollectorSyncStatusContext();
  const hasError = useMemo(() => {
    return (
      Object.values(collectorsSyncStatus).length > 0 &&
      collectorsSelectionStatus !== 'collectors.notInstalled' &&
      !Object.values(collectorsSyncStatus).every(
        (i) => i === 'Completed' || i === 'InProgress'
      )
    );
  }, [collectorsSelectionStatus, collectorsSyncStatus]);

  const children = useMemo(() => {
    if (scope === CollectorScope.Tenant) return <CompanyCollectorsTab />;
    else if (scope === CollectorScope.User) return <UserCollectorsTab />;
    else if (scope === CollectorScope.All)
      return <AllCollectorsTab hasError={hasError} />;
    else return <InstalledCollectorsTab isAdmin={isAdmin} />;
  }, [scope, hasError, isAdmin]);
  return (
    <>
      {isTiwUnifiedCollectorsTab ? (
        <Tabs
          value={scope}
          indicatorColor="primary"
          textColor="primary"
          className={classNames(classes.tabs, classes.indicator)}
          TabIndicatorProps={{
            className: classes.indicator,
            children: <span />
          }}
        >
          <Tab
            value={CollectorScope.All}
            data-testid={'all-collectors-tab'}
            component={Link}
            to="/collectors/#home"
            className={classes.tab}
            label={
              <Badge
                color="secondary"
                variant="dot"
                className={classes.badge}
                invisible={!hasError}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left'
                }}
              >
                <FormattedMessage id="collectorTabs.allCollectors" />
              </Badge>
            }
          />

          <Tab
            value={INSTALLED_TAB_ID}
            data-testid={'installed-collectors-tab'}
            component={Link}
            to="/collectors/#installed"
            className={classes.tab}
            label={<FormattedMessage id="collectorTabs.installed" />}
          />
        </Tabs>
      ) : (
        <Tabs
          value={scope}
          indicatorColor="primary"
          textColor="primary"
          className={classNames(classes.tabs, classes.indicator)}
          TabIndicatorProps={{
            className: classes.indicator,
            children: <span />
          }}
        >
          {isAdmin && !isTiwUnifiedCollectorsTab ? (
            <Tab
              value={CollectorScope.Tenant}
              data-testid={'company-collectors-tab'}
              component={Link}
              to="/collectors/#company"
              label={<FormattedMessage id="collectorTabs.companyCollectors" />}
            />
          ) : null}

          <Tab
            value={CollectorScope.User}
            data-testid={'user-collectors-tab'}
            component={Link}
            to="/collectors/#user"
            label={<FormattedMessage id="collectorTabs.userCollectors" />}
          />

          <Tab
            value={INSTALLED_TAB_ID}
            data-testid={'installed-collectors-tab'}
            component={Link}
            to="/collectors/#installed"
            label={<FormattedMessage id="collectorTabs.installedCollectors" />}
          />
        </Tabs>
      )}

      <TabPanel>{children}</TabPanel>
    </>
  );
});

CollectorListTabs.displayName = 'CollectorListTabs';
